import React from "react";
import TransactionPrintDetails from "./TransactionPrintDetails";
import { Actions, Box, Button, Drawer, IconPrint } from "braid-design-system";

interface TransactionPrintDrawerProps {
    transactionId: number;
    onlyCredits?: boolean;
    onlyDebits?: boolean;
    doPrint?: boolean;
    open: boolean;
    onClose: () => void;
}

const TransactionPrintDrawer: React.FC<TransactionPrintDrawerProps> = ({
    transactionId,
    onlyCredits = false,
    onlyDebits = false,
    doPrint = false,
    open,
    onClose
}: TransactionPrintDrawerProps) => {
    const [printFn, setPrintFn] = React.useState<() => void>(() => () => { });
    const [printLang, setPrintLang] = React.useState('si');
    const [availableSubSections, setAvailableSubSections] = React.useState<string[]>([]);
    const [activeSubSection, setActiveSubSection] = React.useState('');
    const [invoiceInfo, setInvoiceInfo] = React.useState<{ isInvoice: boolean; invoiceHeaderId: number | null } | null>(null);
    const [showInvoice, setShowInvoice] = React.useState(false);

    // Reset the invoice view whenever the drawer is opened for another transaction
    React.useEffect(() => {
        setShowInvoice(false);
        setInvoiceInfo(null);
    }, [transactionId]);

    // Also reset when the drawer closes so reopening starts from the transaction
    React.useEffect(() => {
        if (!open) {
            setShowInvoice(false);
            setInvoiceInfo(null);
        }
    }, [open]);

    const activeTransactionId = showInvoice && invoiceInfo?.invoiceHeaderId
        ? invoiceInfo.invoiceHeaderId
        : transactionId;
    const viewingInvoice = Boolean(showInvoice && invoiceInfo?.invoiceHeaderId);
    const canShowInvoiceButton = !viewingInvoice
        && !invoiceInfo?.isInvoice
        && Boolean(invoiceInfo?.invoiceHeaderId);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            position="right"
            title={viewingInvoice ? "Invoice Print View" : "Transaction Print View"}
            width="large"
        >
            <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
                <Box paddingBottom="small" paddingX="small" className="no-print" display="flex" gap="small" alignItems="center" style={{ borderBottom: '1px solid #eee', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>Language:</span>
                    <select
                        value={printLang}
                        onChange={(e) => setPrintLang(e.target.value)}
                        style={{
                            padding: '4px 8px',
                            fontSize: '13px',
                            borderRadius: '4px',
                            border: '1px solid #c8ccd4',
                            backgroundColor: '#fff',
                            color: '#2b303a',
                            outline: 'none',
                            cursor: 'pointer',
                            height: '32px',
                            fontWeight: 500,
                        }}
                    >
                        <option value="en">English (EN)</option>
                        <option value="si">Sinhala (SI)</option>
                        <option value="ta">Tamil (TA)</option>
                    </select>

                    {availableSubSections.length > 0 && (
                        <>
                            <span style={{ fontSize: '13px', fontWeight: 500, marginLeft: '15px' }}>Sub Section:</span>
                            <select
                                value={activeSubSection}
                                onChange={(e) => setActiveSubSection(e.target.value)}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: '13px',
                                    borderRadius: '4px',
                                    border: '1px solid #c8ccd4',
                                    backgroundColor: '#fff',
                                    color: '#2b303a',
                                    outline: 'none',
                                    cursor: 'pointer',
                                    height: '32px',
                                    fontWeight: 500,
                                }}
                            >
                                <option value="">Default</option>
                                {availableSubSections.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </>
                    )}
                </Box>
                <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0, paddingRight: '4px' }}>
                    <TransactionPrintDetails
                        transactionId={activeTransactionId}
                        onlyCredits={onlyCredits}
                        onlyDebits={onlyDebits}
                        doPrint={doPrint}
                        onPrintReady={setPrintFn}
                        lang={printLang}
                        onSubSectionsLoaded={setAvailableSubSections}
                        selectedSubSectionProp={activeSubSection}
                        onSubSectionChangeProp={setActiveSubSection}
                        onLanguageSelected={setPrintLang}
                        onInvoiceInfoLoaded={setInvoiceInfo}
                    />
                </div>
                <Box
                    className="no-print"
                    padding={"xsmall"}
                    background="surface"
                    display="flex"
                    justifyContent="flexEnd"
                    style={{
                        borderTop: "1px solid #eee",
                        paddingTop: "10px",
                        marginTop: "10px"
                    }}
                >
                    <Actions>
                        {canShowInvoiceButton && (
                            <Button variant="ghost" tone="brandAccent" onClick={() => setShowInvoice(true)}>Invoice</Button>
                        )}
                        {viewingInvoice && (
                            <Button variant="ghost" onClick={() => setShowInvoice(false)}>Back to Transaction</Button>
                        )}
                        <Button variant="solid" tone="formAccent" icon={<IconPrint />} onClick={() => printFn()}>Print</Button>
                    </Actions>
                </Box>
            </div>
        </Drawer >
    );
}

export default TransactionPrintDrawer;